// status-progress-ticket-rows.ts
//
// Progress panel P1: `progress.tickets[]` — one row per `.avatar/tickets/*.json`
// ticket, plus (beta.2, DF-P4-1) each controller-reserved review candidate the
// journal has seen, flagged `controller_reserved`. The three counters come AS-IS from the operating table
// (`TicketOperatingRow.physicalRuns/countedFailures/attemptTotal`, B7: the
// ceiling is of counted failures, never a "a/b runs" fraction); times, session
// ids and cost from the journal facts; agent/skill/playbook from the adapter
// row (R3), `ticket.agent` first. Pure.
import { COMMAND_ADAPTERS } from "./command-adapter.js";
import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import type { TicketOperatingRow } from "./status-operating-table.js";
import { type ProgressJournalFacts, toProgressAttempt } from "./status-progress-journal-facts.js";
import { CLOSED_TICKET_STATUSES } from "./status-progress-journey-derivation.js";
import type { ProgressTicket } from "./status-progress-types.js";

function hintsFor(
  ticket: Ticket,
): Pick<ProgressTicket, "agent_hints" | "skill_hints" | "playbook"> {
  const adapter = COMMAND_ADAPTERS.get(ticket.command_adapter);
  const agents = [
    ...(ticket.agent === undefined ? [] : [ticket.agent]),
    ...(adapter?.agentHints ?? []),
  ];
  return {
    agent_hints: [...new Set(agents)],
    skill_hints: [...(adapter?.skillHints ?? [])],
    ...(adapter?.playbook === undefined ? {} : { playbook: adapter.playbook }),
  };
}

export function buildProgressTickets(input: {
  tickets: readonly Ticket[];
  rows: readonly TicketOperatingRow[];
  facts: ProgressJournalFacts;
  /** recovery ticket id → source ticket id (from the recovery ledger, never the label alone). */
  recoveryOf: ReadonlyMap<string, string>;
}): ProgressTicket[] {
  const rowById = new Map(input.rows.map((r) => [r.id, r]));
  return input.tickets.map((ticket) => {
    const row = rowById.get(ticket.id);
    const status = row?.status ?? "todo";
    const times = input.facts.timesByTicket.get(ticket.id);
    const cost = input.facts.costByTicket.get(ticket.id);
    const recoveryOf = input.recoveryOf.get(ticket.id);
    const finishedAt = CLOSED_TICKET_STATUSES.has(status) ? times?.last_change_at : undefined;
    return {
      id: ticket.id,
      title: ticket.title,
      kind: ticket.kind,
      slice_id: ticket.slice_id,
      status,
      depends_on: [...ticket.depends_on],
      adapter: ticket.command_adapter,
      executor: ticket.executor,
      ...hintsFor(ticket),
      physical_runs: row?.physicalRuns ?? 0,
      counted_failures: row?.countedFailures ?? 0,
      ...(row?.attemptTotal === undefined ? {} : { max_counted_failures: row.attemptTotal }),
      ...(times?.started_at === undefined ? {} : { started_at: times.started_at }),
      ...(finishedAt === undefined ? {} : { finished_at: finishedAt }),
      ...(cost === undefined ? {} : { cost_usd: cost }),
      ...(row?.lastVerdictCategory === undefined
        ? {}
        : { last_verdict_category: row.lastVerdictCategory }),
      last_verdict_reasons: [...(row?.lastVerdictReasons ?? [])],
      ...(recoveryOf === undefined ? {} : { recovery_of: recoveryOf }),
      ...(isControllerReservedTicketId(ticket.id) ? { controller_reserved: true as const } : {}),
      attempts: (input.facts.attemptsByTicket.get(ticket.id) ?? []).map(toProgressAttempt),
    };
  });
}
